import React, { useEffect, useMemo, useState } from "react";
import {
  ShieldAlert, BadgeCheck, Ban, RotateCcw, Check, X, ScrollText, FileText, Eye,
  Activity, Users, Building2, Home, ClipboardList, Inbox, Gauge, AlertTriangle,
  Search, ChevronRight, Tags, Loader2, MapPin, User as UserIcon, Mail, BarChart3, Megaphone,
} from "lucide-react";
import { AnalyticsView } from "@/components/AdminAnalytics";
import { Navbar } from "@/components/Navbar";
import { Fade } from "@/components/proptech";
import { api, apiError, timeAgo, peso } from "@/lib/api";
import { propertyTypeLabel } from "@/lib/taxonomy";
import AdminListingTagging from "@/components/AdminListingTagging";
import MonetizationConsole from "@/components/admin/MonetizationConsole";
import CommunicationsConsole from "@/components/admin/CommunicationsConsole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

/* Guards a section render so an uncaught error never blanks the whole Admin Control Center. */
class SectionErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidUpdate(prev) { if (prev.sectionKey !== this.props.sectionKey && this.state.error) this.setState({ error: null }); }
  render() {
    if (this.state.error) {
      return (
        <div className="bg-critical-soft border border-critical/30 rounded-xl p-6" data-testid="admin-section-error">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-critical shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h3 className="font-semibold text-slate-900">This section failed to load</h3>
              <p className="text-sm text-slate-600 mt-1">Something went wrong while rendering this panel. The rest of the Admin Control Center is still available in the sidebar.</p>
              <Button data-testid="admin-section-error-retry" onClick={() => this.setState({ error: null })} size="sm" variant="outline" className="mt-3 rounded-lg">Try again</Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const VERIF_STYLE = {
  UNVERIFIED: "bg-attention-soft text-attention", PROFESSIONAL_VERIFICATION_PENDING: "bg-info-soft text-info",
  PROFESSIONAL_VERIFIED: "bg-success-soft text-success", VERIFICATION_REJECTED: "bg-critical-soft text-critical",
};
const STATUS_STYLE = {
  Active: "bg-success-soft text-success", Archived: "bg-slate-100 text-slate-500",
  Paused: "bg-attention-soft text-attention", Unavailable: "bg-slate-100 text-slate-500",
  Sold: "bg-info-soft text-info", Leased: "bg-info-soft text-info",
};
const VIS_STYLE = {
  PUBLIC_MARKETPLACE: "bg-emerald-100 text-emerald-700", NETWORK_ONLY: "bg-indigo-100 text-indigo-700",
  PRIVATE: "bg-slate-100 text-slate-500",
};
const LT_LABEL = { "For Sale": "For Sale", "For Rent": "For Rent", "Unit Hosting": "Stays / Unit Hosting" };
const LEAD_STYLE = { NEW: "bg-accent/15 text-accent", CONTACTED: "bg-blue-100 text-blue-700", CLOSED: "bg-slate-100 text-slate-500" };

const NAV = [
  { key: "pulse", label: "Pulse", icon: Gauge },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "users", label: "Users & Professionals", icon: Users },
  { key: "verification", label: "Verification", icon: BadgeCheck },
  { key: "listings", label: "Listings", icon: Home },
  { key: "tagging", label: "Dev / Project Tagging", icon: Tags },
  { key: "submissions", label: "Sell Submissions", icon: ClipboardList },
  { key: "inquiries", label: "Inquiries & Leads", icon: Inbox },
  { key: "communications", label: "Communications", icon: Mail },
  { key: "monetization", label: "Advertising & Monetization", icon: Megaphone },
  { key: "activity", label: "Activity", icon: Activity },
];

const Pill = ({ className = "", children }) => (
  <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${className}`}>{children}</span>
);

function Metric({ label, value, sub, testid }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Admin({ initialSection = "pulse" }) {
  const [section, setSection] = useState(() => new URLSearchParams(window.location.search).get("section") || initialSection);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [vreqs, setVreqs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(true);
  const [subsError, setSubsError] = useState(false);
  const [pulse, setPulse] = useState(null);
  const [repSel, setRepSel] = useState({});

  const loadSubs = () => {
    setSubsLoading(true);
    setSubsError(false);
    return api.get("/admin/property-submissions", { params: { state: "SUBMITTED" } })
      .then((r) => setSubs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSubsError(true))
      .finally(() => setSubsLoading(false));
  };

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/audit-logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/admin/verification-requests").then((r) => setVreqs(r.data)).catch(() => {});
    loadSubs();
    api.get("/admin/pulse").then((r) => setPulse(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); } };
  const eligiblePros = users.filter((u) => (u.account_type || "PROFESSIONAL") !== "CONSUMER" && u.role !== "ADMIN" && u.account_status !== "SUSPENDED" && u.verification_status === "PROFESSIONAL_VERIFIED");
  const approveSub = async (id) => {
    const professional_id = repSel[id];
    if (!professional_id) { toast.error("Select a representing professional first."); return; }
    await act(() => api.post(`/admin/property-submissions/${id}/approve`, { professional_id }), "Submission approved & published");
  };
  const rejectSub = async (id, reason) => {
    await act(() => api.post(`/admin/property-submissions/${id}/reject`, { reason: (reason || "").trim() }), "Submission rejected");
  };
  const viewDoc = async (uidv, docId) => {
    try { const res = await api.get(`/verification/document/${uidv}/${docId}`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main data-testid="admin-page" className="mx-auto max-w-[1600px] px-4 sm:px-6 py-8">
      <div className="mb-6">
        <p className="eyebrow flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Admin Control Center</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">PropertyConnect Command</h1>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-6 items-start">
        {/* Sidebar */}
        <nav className="bg-[#0F2C59] rounded-2xl p-2 lg:sticky lg:top-4 flex lg:flex-col gap-1 overflow-x-auto" data-testid="cc-nav">
          {NAV.map((n) => {
            const active = section === n.key;
            const badge = n.key === "verification" ? vreqs.length : n.key === "submissions" ? subs.length : 0;
            return (
              <button key={n.key} data-testid={`cc-nav-${n.key}`} onClick={() => setSection(n.key)}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap w-full ${active ? "bg-white text-[#0F2C59]" : "text-white/70 hover:text-white hover:bg-white/10"}`}>
                <n.icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 text-left">{n.label}</span>
                {badge > 0 && <span className={`text-[10px] rounded-full px-1.5 font-bold ${active ? "bg-accent text-accent-foreground" : "bg-accent text-accent-foreground"}`}>{badge}</span>}
              </button>
            );
          })}
        </nav>

        <div className="min-w-0">
          <SectionErrorBoundary sectionKey={section}>
            {section === "pulse" && <PulseView pulse={pulse} goto={setSection} />}
            {section === "analytics" && <AnalyticsView />}
            {section === "users" && <UsersView users={users} act={act} logs={logs} />}
            {section === "verification" && <VerificationView vreqs={vreqs} act={act} viewDoc={viewDoc} />}
            {section === "listings" && <ListingsView users={users} act={act} />}
            {section === "tagging" && <AdminListingTagging />}
            {section === "submissions" && <SubmissionsView subs={subs} loading={subsLoading} error={subsError} onRetry={loadSubs} eligiblePros={eligiblePros} repSel={repSel} setRepSel={setRepSel} approveSub={approveSub} rejectSub={rejectSub} />}
            {section === "inquiries" && <InquiriesView users={users} />}
            {section === "communications" && <CommunicationsConsole />}
            {section === "monetization" && <MonetizationConsole />}
            {section === "activity" && <ActivityView logs={logs} />}
          </SectionErrorBoundary>
        </div>
      </div>
      </main>
    </div>
  );
}

/* ------------------------------- PULSE ---------------------------------- */
function PulseView({ pulse, goto }) {
  if (!pulse) return <div className="text-slate-500 text-sm" data-testid="pulse-loading">Loading operational snapshot…</div>;
  const a = pulse.accounts, t = pulse.trust, m = pulse.marketplace, s = pulse.sell, l = pulse.leads, at = pulse.attention;
  const attention = [
    { n: at.pending_verification, label: "Verification applications pending review", to: "verification" },
    { n: at.sell_submitted, label: "Sell submissions awaiting review", to: "submissions" },
    { n: at.new_inquiries, label: "New inquiries awaiting first contact", to: "inquiries" },
    { n: at.risky_public_listings, label: "Public listings with an inactive/ineligible manager", to: "listings" },
  ].filter((x) => x.n > 0);

  return (
    <Fade>
      <div className="space-y-6" data-testid="pulse-view">
        {/* Traffic & Growth (compact executive summary; click to open Analytics) */}
        {pulse.traffic && (
          <section data-testid="traffic-growth">
            <div className="flex items-center gap-2 mb-3"><BarChart3 className="h-4 w-4 text-accent" /><h2 className="font-display text-lg font-semibold text-slate-900">Traffic &amp; Growth</h2></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { testid: "tg-visitors-today", label: "Visitors Today", value: pulse.traffic.visitors_today },
                { testid: "tg-visitors-7d", label: "Visitors 7D", value: pulse.traffic.visitors_7d },
                { testid: "tg-visitors-30d", label: "Visitors 30D", value: pulse.traffic.visitors_30d },
                { testid: "tg-guests-30d", label: "Guests 30D", value: pulse.traffic.guests_30d },
                { testid: "tg-pageviews-30d", label: "Page Views 30D", value: pulse.traffic.pageviews_30d },
                { testid: "tg-registrations-30d", label: "New Regs 30D", value: pulse.traffic.new_registrations_30d },
              ].map((c) => (
                <button key={c.testid} data-testid={c.testid} onClick={() => goto("analytics")}
                  className="bg-card border border-border rounded-xl p-4 text-left hover:border-accent transition-colors">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">{c.label}</div>
                  <div className="font-display text-2xl font-bold text-slate-900 mt-1">{c.value ?? 0}</div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Attention Required */}
        <div className="bg-card border border-border rounded-2xl p-5" data-testid="attention-required">
          <div className="flex items-center gap-2 mb-3"><AlertTriangle className="h-4 w-4 text-accent" /><h2 className="font-display text-lg font-semibold text-slate-900">Attention Required</h2></div>
          {attention.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="attention-empty">All clear — nothing needs administrator action right now.</p>
          ) : (
            <div className="space-y-2">
              {attention.map((x) => (
                <button key={x.to} data-testid={`attention-${x.to}`} onClick={() => goto(x.to)}
                  className="w-full flex items-center gap-3 bg-secondary/50 hover:bg-secondary rounded-xl px-3 py-2.5 text-left transition-colors">
                  <span className="font-display text-xl font-bold text-accent w-8">{x.n}</span>
                  <span className="flex-1 text-sm text-slate-700">{x.label}</span>
                  <span className="text-xs font-semibold text-[#0F2C59] flex items-center gap-1">Review <ChevronRight className="h-3.5 w-3.5" /></span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Accounts */}
        <section>
          <h3 className="text-[11px] uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Accounts</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Metric testid="pm-total" label="Total" value={a.total} />
            <Metric testid="pm-consumers" label="Consumers" value={a.consumers} />
            <Metric testid="pm-pros" label="Professionals" value={a.professionals} />
            <Metric testid="pm-active" label="Active" value={a.active} />
            <Metric testid="pm-suspended" label="Suspended" value={a.suspended} />
            <Metric testid="pm-admins" label="Admins" value={a.admins} />
          </div>
        </section>

        {/* Trust */}
        <section>
          <h3 className="text-[11px] uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><BadgeCheck className="h-3.5 w-3.5" /> Trust</h3>
          <div className="grid grid-cols-3 gap-3">
            <Metric testid="pm-verified" label="Verified Pros" value={t.verified} />
            <Metric testid="pm-pending" label="Pending Verification" value={t.pending} />
            <Metric testid="pm-rejected" label="Rejected" value={t.rejected} />
          </div>
        </section>

        {/* Marketplace */}
        <section>
          <h3 className="text-[11px] uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> Marketplace</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            <Metric testid="pm-public" label="Public" value={m.public_total} />
            <Metric testid="pm-sale" label="For Sale" value={m.for_sale} />
            <Metric testid="pm-rent" label="For Rent" value={m.for_rent} />
            <Metric testid="pm-hosting" label="Stays" value={m.unit_hosting} />
            <Metric testid="pm-network" label="Network Only" value={m.network_only} />
            <Metric testid="pm-private" label="Private" value={m.private} />
            <Metric testid="pm-paused" label="Paused/Unavail." value={m.paused_unavailable} />
          </div>
        </section>

        {/* Sell + Leads */}
        <div className="grid md:grid-cols-2 gap-6">
          <section>
            <h3 className="text-[11px] uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><ClipboardList className="h-3.5 w-3.5" /> Sell Pipeline</h3>
            <div className="grid grid-cols-3 gap-3">
              <Metric testid="pm-sub" label="Submitted" value={s.submitted} />
              <Metric testid="pm-appr" label="Approved" value={s.approved} />
              <Metric testid="pm-rej" label="Rejected" value={s.rejected} />
            </div>
          </section>
          <section>
            <h3 className="text-[11px] uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Inbox className="h-3.5 w-3.5" /> Leads</h3>
            <div className="grid grid-cols-3 gap-3">
              <Metric testid="pm-leads-new" label="New" value={l.new} />
              <Metric testid="pm-leads-contacted" label="Contacted" value={l.contacted} />
              <Metric testid="pm-leads-closed" label="Closed" value={l.closed} />
              <Metric testid="pm-leads-general" label="General" value={l.general} />
              <Metric testid="pm-leads-viewing" label="Viewing" value={l.viewing} />
              <Metric testid="pm-leads-availability" label="Availability" value={l.availability} />
            </div>
          </section>
        </div>

        {/* Recent Activity */}
        <section>
          <h3 className="text-[11px] uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> Recent Activity</h3>
          <div className="bg-card border border-border rounded-xl divide-y divide-border" data-testid="pulse-activity">
            {(pulse.recent_activity || []).length === 0 && <p className="p-4 text-sm text-slate-500">No recent administrator activity.</p>}
            {(pulse.recent_activity || []).slice(0, 6).map((r) => (
              <div key={r.id} className="px-4 py-2.5 flex items-center gap-2.5 text-sm">
                <Check className="h-3.5 w-3.5 text-accent shrink-0" />
                <span className="font-medium">{r.admin_name}</span>
                <span className="text-slate-500">{(r.action || "").replace(/_/g, " ").toLowerCase()}</span>
                <span className="text-slate-400 truncate">{r.target_type} · {r.target_id}</span>
                <span className="ml-auto text-[11px] text-slate-400 whitespace-nowrap">{timeAgo(r.created_at)}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Fade>
  );
}

/* --------------------------- USERS & PROFESSIONALS ----------------------- */
function UsersView({ users, act, logs }) {
  const [q, setQ] = useState("");
  const [atype, setAtype] = useState("ALL");
  const [vstatus, setVstatus] = useState("ALL");
  const [astatus, setAstatus] = useState("ALL");
  const [detail, setDetail] = useState(null);

  const filtered = useMemo(() => users.filter((u) => {
    if (atype !== "ALL" && (u.account_type || "PROFESSIONAL") !== atype) return false;
    if (vstatus !== "ALL" && (u.verification_status || "UNVERIFIED") !== vstatus) return false;
    if (astatus !== "ALL" && (u.account_status || "ACTIVE") !== astatus) return false;
    if (q) { const s = q.toLowerCase(); if (!(u.name || "").toLowerCase().includes(s) && !(u.email || "").toLowerCase().includes(s) && !(u.organization_name || "").toLowerCase().includes(s)) return false; }
    return true;
  }), [users, q, atype, vstatus, astatus]);

  return (
    <Fade>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, organization" className="pl-9" />
        </div>
        <Select value={atype} onValueChange={setAtype}><SelectTrigger data-testid="filter-account-type" className="w-[150px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ALL">All types</SelectItem><SelectItem value="PROFESSIONAL">Professional</SelectItem><SelectItem value="CONSUMER">Consumer</SelectItem></SelectContent></Select>
        <Select value={vstatus} onValueChange={setVstatus}><SelectTrigger data-testid="filter-verification" className="w-[170px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ALL">All verification</SelectItem><SelectItem value="PROFESSIONAL_VERIFIED">Verified</SelectItem><SelectItem value="PROFESSIONAL_VERIFICATION_PENDING">Pending</SelectItem><SelectItem value="UNVERIFIED">Unverified</SelectItem><SelectItem value="VERIFICATION_REJECTED">Rejected</SelectItem></SelectContent></Select>
        <Select value={astatus} onValueChange={setAstatus}><SelectTrigger data-testid="filter-account-status" className="w-[140px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ALL">All statuses</SelectItem><SelectItem value="ACTIVE">Active</SelectItem><SelectItem value="SUSPENDED">Suspended</SelectItem></SelectContent></Select>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden overflow-x-auto pc-scroll">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-secondary/50 text-slate-500 text-left">
            <tr>{["Name", "Type", "Role", "Organization", "Verification", "Listings", "Status", "Actions"].map((h) => <th key={h} className="px-3 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400">No accounts match these filters.</td></tr>}
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-t border-border hover:bg-secondary/30">
                <td className="px-3 py-2.5 whitespace-nowrap"><button onClick={() => setDetail(u)} data-testid={`user-open-${u.id}`} className="flex items-center gap-2 hover:underline"><img src={u.avatar} alt="" className="h-7 w-7 rounded-full object-cover" /><span className="font-medium">{u.name}</span></button></td>
                <td className="px-3 py-2.5"><Pill className={(u.account_type || "PROFESSIONAL") === "CONSUMER" ? "bg-slate-100 text-slate-600" : "bg-indigo-100 text-indigo-700"}>{(u.account_type || "PROFESSIONAL") === "CONSUMER" ? "Consumer" : "Professional"}</Pill></td>
                <td className="px-3 py-2.5 text-slate-600 max-w-[130px] truncate">{u.role_label || u.role}</td>
                <td className="px-3 py-2.5 text-slate-500 max-w-[130px] truncate">{u.organization_name || u.agency || "—"}</td>
                <td className="px-3 py-2.5"><Pill className={VERIF_STYLE[u.verification_status] || "bg-secondary"}>{(u.verification_status || "").replace(/_/g, " ")}</Pill></td>
                <td className="px-3 py-2.5">{u.active_listings}</td>
                <td className="px-3 py-2.5"><span className={`text-[11px] font-semibold ${u.account_status === "SUSPENDED" ? "text-red-600" : "text-emerald-600"}`}>{u.account_status}</span></td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1.5">
                    <button title="Verify" data-testid={`verify-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/verify`), "Verified")} className="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-100"><BadgeCheck className="h-4 w-4" /></button>
                    {u.account_status === "SUSPENDED" ? (
                      <button title="Reactivate" data-testid={`reactivate-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/reactivate`), "Reactivated")} className="h-8 w-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-100"><RotateCcw className="h-4 w-4" /></button>
                    ) : (
                      <AlertDialog>
                        <AlertDialogTrigger asChild><button title="Suspend" data-testid={`suspend-${u.id}`} className="h-8 w-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100"><Ban className="h-4 w-4" /></button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Suspend {u.name}?</AlertDialogTitle><AlertDialogDescription>They will lose access to all protected marketplace functionality until reactivated.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction data-testid={`confirm-suspend-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/suspend`), "Suspended")} className="bg-red-600 hover:bg-red-700">Suspend</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="user-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><img src={detail.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />{detail.name}</DialogTitle><DialogDescription>Operational account summary — administrator view.</DialogDescription></DialogHeader>
              <div className="space-y-2 text-sm">
                <Row k="Account type" v={(detail.account_type || "PROFESSIONAL") === "CONSUMER" ? "Consumer" : "Professional"} />
                <Row k="Email" v={detail.email} />
                <Row k="Role" v={detail.role_label || detail.role} />
                <Row k="Organization" v={detail.organization_name || detail.agency || "—"} />
                <Row k="Account status" v={detail.account_status} />
                <Row k="Verification" v={(detail.verification_status || "").replace(/_/g, " ")} />
                <Row k="Service areas" v={(detail.service_areas || []).join(", ") || "—"} />
                <Row k="Active listings" v={detail.active_listings} />
                <Row k="Active buyers" v={detail.active_buyers} />
                <Row k="Joined" v={detail.created_at ? timeAgo(detail.created_at) : "—"} />
              </div>
              <div className="mt-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Recent related admin activity</div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {logs.filter((l) => l.target_id === detail.id).slice(0, 5).map((l) => (
                    <div key={l.id} className="text-xs text-slate-500">{(l.action || "").replace(/_/g, " ").toLowerCase()} · {timeAgo(l.created_at)}</div>
                  ))}
                  {logs.filter((l) => l.target_id === detail.id).length === 0 && <div className="text-xs text-slate-400">None recorded.</div>}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Fade>
  );
}
const Row = ({ k, v }) => <div className="flex gap-3"><span className="text-slate-400 w-32 shrink-0">{k}</span><span className="text-slate-700 break-words">{v}</span></div>;

/* --------------------------- VERIFICATION ------------------------------- */
function VerificationView({ vreqs, act, viewDoc }) {
  return (
    <Fade>
      {vreqs.length === 0 && <p className="text-slate-500 text-sm" data-testid="verification-empty">No pending verification requests.</p>}
      <div className="grid md:grid-cols-2 gap-4" data-testid="verification-requests">
        {vreqs.map((v) => (
          <div key={v.id} data-testid={`vreq-${v.id}`} className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-3">
              <img src={v.avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
              <div><div className="font-semibold text-slate-900">{v.name}</div><div className="text-xs text-slate-500">{v.role_label} · {v.agency}</div></div>
            </div>
            <div className="mt-3 space-y-1 text-sm text-slate-600">
              <div><span className="text-slate-400">PRC #:</span> {v.prc_number || "—"}</div>
              <div><span className="text-slate-400">Broker affiliation:</span> {v.broker_affiliation || "—"}</div>
            </div>
            <div className="mt-3">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Documents</p>
              {v.documents.length === 0 && <p className="text-xs text-slate-400">No documents uploaded.</p>}
              {v.documents.map((d) => (
                <button key={d.id} data-testid={`viewdoc-${d.id}`} onClick={() => viewDoc(v.id, d.id)} className="flex items-center gap-2 text-sm text-accent font-medium hover:underline"><FileText className="h-4 w-4" />{d.filename}<Eye className="h-3.5 w-3.5" /></button>
              ))}
            </div>
            <div className="flex gap-2 mt-4 pt-3 border-t border-border">
              <Button data-testid={`vapprove-${v.id}`} onClick={() => act(() => api.post(`/admin/users/${v.id}/verify`), "Approved")} size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-lg">Verify</Button>
              <Button data-testid={`vreject-${v.id}`} onClick={() => act(() => api.post(`/admin/users/${v.id}/reject`, { reason: "Documents insufficient" }), "Rejected")} size="sm" variant="outline" className="rounded-lg">Reject</Button>
              <Button data-testid={`vresubmit-${v.id}`} onClick={() => act(() => api.post(`/admin/users/${v.id}/request-resubmission`, { reason: "Please re-upload a clearer PRC document" }), "Resubmission requested")} size="sm" variant="ghost" className="rounded-lg">Request info</Button>
            </div>
          </div>
        ))}
      </div>
    </Fade>
  );
}

/* ------------------------------ LISTINGS -------------------------------- */
function ListingsView({ users, act }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lt, setLt] = useState("ALL");
  const [st, setSt] = useState("ALL");
  const [vis, setVis] = useState("ALL");
  const [q, setQ] = useState("");

  const fetchRows = () => {
    setLoading(true);
    const params = {};
    if (lt !== "ALL") params.listing_type = lt;
    if (st !== "ALL") params.status = st;
    if (vis !== "ALL") params.visibility = vis;
    if (q) params.q = q;
    api.get("/admin/listings", { params }).then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [lt, st, vis]);

  const setStatus = (id, status) => act(() => api.patch(`/properties/${id}/status`, { status }), "Status updated").then(fetchRows);
  const setVisibility = (id, visibility) => act(() => api.patch(`/properties/${id}/visibility`, { visibility }), "Visibility updated").then(fetchRows);

  return (
    <Fade>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="listing-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchRows()} placeholder="Search title or city, press Enter" className="pl-9" />
        </div>
        <Select value={lt} onValueChange={setLt}><SelectTrigger data-testid="filter-listing-type" className="w-[180px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ALL">All types</SelectItem><SelectItem value="For Sale">For Sale</SelectItem><SelectItem value="For Rent">For Rent</SelectItem><SelectItem value="Unit Hosting">Stays / Unit Hosting</SelectItem></SelectContent></Select>
        <Select value={st} onValueChange={setSt}><SelectTrigger data-testid="filter-status" className="w-[150px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ALL">All statuses</SelectItem>{["Active", "Paused", "Unavailable", "Sold", "Leased", "Archived"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={vis} onValueChange={setVis}><SelectTrigger data-testid="filter-visibility" className="w-[160px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ALL">All visibility</SelectItem><SelectItem value="PUBLIC_MARKETPLACE">Public</SelectItem><SelectItem value="NETWORK_ONLY">Network Only</SelectItem><SelectItem value="PRIVATE">Private</SelectItem></SelectContent></Select>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden overflow-x-auto pc-scroll">
        <table className="w-full text-sm" data-testid="admin-listings-table">
          <thead className="bg-secondary/50 text-slate-500 text-left">
            <tr>{["Title", "Type", "Location", "Price", "Manager", "Status", "Visibility", "Actions"].map((h) => <th key={h} className="px-3 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400">Loading listings…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400">No listings match these filters.</td></tr>}
            {!loading && rows.map((p) => (
              <tr key={p.id} data-testid={`admin-listing-${p.id}`} className="border-t border-border hover:bg-secondary/30">
                <td className="px-3 py-2.5 max-w-[150px]">
                  <div className="font-medium text-slate-800 truncate">{p.title}</div>
                  {p.trust_warning && <div data-testid={`listing-trust-${p.id}`} className="mt-0.5 flex items-center gap-1 text-[11px] text-red-600 font-semibold"><AlertTriangle className="h-3 w-3" />{p.trust_reason}</div>}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap"><Pill className={p.listing_type === "Unit Hosting" ? "bg-teal-100 text-teal-700" : "bg-slate-100 text-slate-600"}>{p.listing_type === "Unit Hosting" ? "Stays" : p.listing_type}</Pill>{p.origin === "CONSUMER_SUBMISSION" && <Pill className="ml-1 bg-amber-100 text-amber-700">Owner</Pill>}</td>
                <td className="px-3 py-2.5 text-slate-500 max-w-[120px] truncate">{p.city}{p.neighborhood ? `, ${p.neighborhood}` : ""}</td>
                <td className="px-3 py-2.5 whitespace-nowrap font-semibold text-slate-800">{p.listing_type === "Unit Hosting" ? `${peso(p.nightly_rate)}/night` : peso(p.price)}{p.listing_type === "Unit Hosting" && <div className="text-[11px] text-slate-400 font-normal">{p.max_guests} guests · {(p.availability_status || "").replace(/_/g, " ").toLowerCase()}</div>}</td>
                <td className="px-3 py-2.5 max-w-[130px] truncate text-slate-600">{p.manager?.name || "—"}{p.manager && p.manager.account_status === "SUSPENDED" && <Pill className="ml-1 bg-red-100 text-red-700">suspended</Pill>}</td>
                <td className="px-3 py-2.5"><Pill className={STATUS_STYLE[p.status] || "bg-secondary"}>{p.status}</Pill></td>
                <td className="px-3 py-2.5"><Pill className={VIS_STYLE[p.visibility] || "bg-secondary"}>{(p.visibility || "").replace(/_/g, " ")}</Pill></td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-col gap-1">
                    <Select value={p.status} onValueChange={(v) => setStatus(p.id, v)}><SelectTrigger data-testid={`listing-status-${p.id}`} className="h-7 w-[116px] text-xs"><SelectValue /></SelectTrigger><SelectContent>{["Active", "Paused", "Unavailable", "Sold", "Leased", "Archived"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                    <Select value={p.visibility} onValueChange={(v) => setVisibility(p.id, v)}><SelectTrigger data-testid={`listing-visibility-${p.id}`} className="h-7 w-[116px] text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="PUBLIC_MARKETPLACE">Public</SelectItem><SelectItem value="NETWORK_ONLY">Network Only</SelectItem><SelectItem value="PRIVATE">Private</SelectItem></SelectContent></Select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Fade>
  );
}

/* --------------------------- SELL SUBMISSIONS --------------------------- */
const relLabel = (r) => (r === "OWNER" ? "Owner" : r === "AUTHORIZED_REPRESENTATIVE" ? "Authorized representative" : r ? String(r).replace(/_/g, " ").toLowerCase() : "—");

function safePhotos(s) {
  const list = Array.isArray(s?.photo_urls) ? s.photo_urls : [];
  const urls = list.map((ph) => (typeof ph === "string" ? ph : (ph?.url || ph?.thumb_url))).filter(Boolean);
  if (!urls.length && s?.image) urls.push(s.image);
  return urls;
}

function SubmissionCard({ s, eligiblePros, repSel, setRepSel, approveSub, rejectSub }) {
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const photos = safePhotos(s);
  const cover = photos[0] || null;
  const title = s?.title || "Untitled submission";
  const loc = [s?.neighborhood, s?.city].filter(Boolean).join(", ") || "Location not provided";
  const bed = s?.bedrooms; const bath = s?.bathrooms;
  const specs = [];
  if (bed !== null && bed !== undefined && bed !== "") specs.push(`${bed}BR`);
  if (bath !== null && bath !== undefined && bath !== "") specs.push(`${bath}BA`);
  const selectedRep = repSel[s.id] || "";

  const doApprove = async () => {
    setBusy(true);
    try { await approveSub(s.id); setConfirmApprove(false); } finally { setBusy(false); }
  };
  const doReject = async () => {
    setBusy(true);
    try { await rejectSub(s.id, reason); setRejectOpen(false); setReason(""); } finally { setBusy(false); }
  };

  return (
    <div data-testid={`submission-review-${s.id}`} className="bg-card border border-border rounded-xl p-5">
      <div className="flex gap-4">
        <div className="h-20 w-24 rounded-lg overflow-hidden bg-slate-100 shrink-0">
          {cover
            ? <img src={cover} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.setAttribute("data-img-failed", "1"); }} />
            : <div className="h-full w-full flex items-center justify-center text-slate-300"><FileText className="h-6 w-6" /></div>}
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate" title={title}>{title}</div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" /> {propertyTypeLabel(s?.property_type) || "—"} · {loc}</div>
          <div className="text-sm font-bold text-slate-800 mt-1">{s?.price ? peso(s.price) : "Price not provided"}</div>
          {specs.length > 0 && <div className="text-[11px] text-slate-400 mt-0.5">{specs.join(" · ")}</div>}
        </div>
      </div>

      {photos.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto" data-testid={`submission-photos-${s.id}`}>
          {photos.slice(0, 6).map((u, i) => (
            <div key={i} className="h-14 w-16 rounded-md overflow-hidden bg-slate-100 shrink-0 flex items-center justify-center">
              <img src={u} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("span"), { className: "text-slate-300" })); }} />
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 text-sm text-slate-600 border-t border-border pt-3 space-y-0.5">
        <div className="flex items-center gap-1"><UserIcon className="h-3.5 w-3.5 text-slate-400 shrink-0" /> <span className="text-slate-400">Submitter:</span> {s?.submitter?.name || "Unknown"}</div>
        <div className="flex items-center gap-1"><Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" /> <span className="text-slate-400">Contact:</span> {s?.submitter?.email || "—"}</div>
        <div><span className="text-slate-400">Relationship:</span> {relLabel(s?.submitter_relationship)}</div>
        <div><span className="text-slate-400">Submitted:</span> {s?.created_at ? timeAgo(s.created_at) : "—"}</div>
      </div>

      <div className="mt-4 pt-3 border-t border-border space-y-2">
        <label className="text-[11px] uppercase tracking-wide text-slate-400">Assign representing professional (verified only)</label>
        {eligiblePros.length === 0
          ? <p className="text-xs text-slate-400" data-testid={`sub-no-pros-${s.id}`}>No verified professionals available to assign.</p>
          : (
            <Select value={selectedRep} onValueChange={(v) => setRepSel((p) => ({ ...p, [s.id]: v }))}>
              <SelectTrigger data-testid={`sub-rep-select-${s.id}`}><SelectValue placeholder="Select a professional" /></SelectTrigger>
              <SelectContent>{eligiblePros.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email || u.id} · {u.role}{u.organization_name ? ` · ${u.organization_name}` : ""} · ✓ Verified</SelectItem>)}</SelectContent>
            </Select>
          )}
        <div className="flex gap-2 pt-1">
          <Button data-testid={`sub-approve-${s.id}`} onClick={() => (selectedRep ? setConfirmApprove(true) : toast.error("Select a representing professional first."))} size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-lg">Approve &amp; Publish</Button>
          <Button data-testid={`sub-reject-${s.id}`} onClick={() => setRejectOpen(true)} size="sm" variant="outline" className="rounded-lg">Reject</Button>
        </div>
      </div>

      {/* Approve confirmation */}
      <AlertDialog open={confirmApprove} onOpenChange={(o) => { if (!busy) setConfirmApprove(o); }}>
        <AlertDialogContent data-testid={`sub-approve-dialog-${s.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve &amp; publish this submission?</AlertDialogTitle>
            <AlertDialogDescription>
              "{title}" will be published to the Public Marketplace and represented by the selected verified professional. This action is logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`sub-approve-cancel-${s.id}`} disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`sub-approve-confirm-${s.id}`} onClick={(e) => { e.preventDefault(); doApprove(); }} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Approve & Publish"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject confirmation with optional reason */}
      <AlertDialog open={rejectOpen} onOpenChange={(o) => { if (!busy) { setRejectOpen(o); if (!o) setReason(""); } }}>
        <AlertDialogContent data-testid={`sub-reject-dialog-${s.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this submission?</AlertDialogTitle>
            <AlertDialogDescription>
              "{title}" will not be published. You can add an optional reason for the internal record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            data-testid={`sub-reject-reason-${s.id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional reason (e.g. incomplete details, unclear photos)…"
            className="min-h-[80px]"
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`sub-reject-cancel-${s.id}`} disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`sub-reject-confirm-${s.id}`} onClick={(e) => { e.preventDefault(); doReject(); }} disabled={busy} className="bg-critical hover:bg-critical/90">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reject Submission"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SubmissionsView({ subs, loading, error, onRetry, eligiblePros, repSel, setRepSel, approveSub, rejectSub }) {
  const list = Array.isArray(subs) ? subs : [];
  return (
    <Fade>
      {loading && (
        <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="submissions-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading sell submissions…
        </div>
      )}
      {!loading && error && (
        <div className="bg-critical-soft border border-critical/30 rounded-xl p-6" data-testid="submissions-error">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-critical shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-slate-900">Couldn't load submissions</h3>
              <p className="text-sm text-slate-600 mt-1">There was a problem fetching sell submissions. Please try again.</p>
              <Button data-testid="submissions-retry" onClick={onRetry} size="sm" variant="outline" className="mt-3 rounded-lg">Retry</Button>
            </div>
          </div>
        </div>
      )}
      {!loading && !error && list.length === 0 && (
        <p className="text-slate-500 text-sm" data-testid="submissions-empty">No pending consumer property submissions.</p>
      )}
      {!loading && !error && list.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="admin-submissions">
          {list.map((s) => (
            <SubmissionCard
              key={s.id}
              s={s}
              eligiblePros={eligiblePros}
              repSel={repSel}
              setRepSel={setRepSel}
              approveSub={approveSub}
              rejectSub={rejectSub}
            />
          ))}
        </div>
      )}
    </Fade>
  );
}

/* --------------------------- INQUIRIES & LEADS -------------------------- */
function InquiriesView({ users }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [lt, setLt] = useState("ALL");
  const [ra, setRa] = useState(null); // {id} restricted-access target
  const [reason, setReason] = useState("");
  const [raData, setRaData] = useState(null);
  const [raBusy, setRaBusy] = useState(false);
  const RA_REASONS = ["Support", "Abuse/Fraud Investigation", "Security", "Data Integrity", "Legal/Compliance", "Other"];
  const doReveal = async () => {
    if (!ra || !reason) return;
    setRaBusy(true);
    try {
      const r = await api.get(`/admin/inquiries/${ra.id}/restricted`, { params: { reason } });
      setRaData(r.data);
    } catch (e) { toast.error("Access failed"); }
    finally { setRaBusy(false); }
  };

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (type !== "ALL") params.inquiry_type = type;
    if (status !== "ALL") params.status = status;
    if (lt !== "ALL") params.listing_type = lt;
    api.get("/admin/inquiries", { params }).then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, [type, status, lt]);

  return (
    <Fade>
      <div className="flex flex-wrap gap-2 mb-4">
        <Select value={type} onValueChange={setType}><SelectTrigger data-testid="filter-inquiry-type" className="w-[180px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ALL">All types</SelectItem>{["GENERAL", "INFORMATION", "PRICE_PAYMENT", "VIEWING_REQUEST", "AVAILABILITY_REQUEST"].map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="filter-inquiry-status" className="w-[140px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ALL">All statuses</SelectItem>{["NEW", "CONTACTED", "CLOSED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={lt} onValueChange={setLt}><SelectTrigger data-testid="filter-inquiry-listing-type" className="w-[180px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ALL">All listing types</SelectItem><SelectItem value="For Sale">For Sale</SelectItem><SelectItem value="For Rent">For Rent</SelectItem><SelectItem value="Unit Hosting">Stays / Unit Hosting</SelectItem></SelectContent></Select>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden overflow-x-auto pc-scroll">
        <p className="text-[11px] text-slate-400 px-3 pt-2">Client names, contact details and messages are <span className="font-semibold text-slate-500">restricted administrative data</span> — use “View Restricted Data” (audited) when access is genuinely required.</p>
        <table className="w-full text-sm" data-testid="admin-inquiries-table">
          <thead className="bg-secondary/50 text-slate-500 text-left">
            <tr>{["Type", "Listing", "Assigned Pro", "Message", "Status", "Age", "Access"].map((h) => <th key={h} className="px-3 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Loading leads…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">No inquiries match these filters.</td></tr>}
            {!loading && rows.map((i) => (
              <tr key={i.id} data-testid={`admin-inquiry-${i.id}`} className="border-t border-border hover:bg-secondary/30">
                <td className="px-3 py-2.5 whitespace-nowrap"><Pill className="bg-slate-100 text-slate-600">{(i.inquiry_type || "").replace(/_/g, " ")}</Pill></td>
                <td className="px-3 py-2.5 whitespace-nowrap text-slate-500">{LT_LABEL[i.listing_type] || i.listing_type || "—"}</td>
                <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{i.professional_name || "—"}</td>
                <td className="px-3 py-2.5 text-[12px] text-slate-400 whitespace-nowrap">{i.has_message ? "message on file" : "—"}</td>
                <td className="px-3 py-2.5"><Pill className={LEAD_STYLE[i.status] || "bg-secondary"}>{i.status}{i.status === "NEW" && " • uncontacted"}</Pill></td>
                <td className="px-3 py-2.5 text-[11px] text-slate-400 whitespace-nowrap">{timeAgo(i.created_at)}</td>
                <td className="px-3 py-2.5 whitespace-nowrap"><Button size="sm" variant="outline" data-testid={`inquiry-reveal-${i.id}`} onClick={() => { setRa({ id: i.id }); setReason(""); setRaData(null); }} className="h-8 text-xs">View Restricted Data</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!ra} onOpenChange={(o) => { if (!o) { setRa(null); setRaData(null); setReason(""); } }}>
        <DialogContent data-testid="restricted-access-dialog">
          <DialogHeader><DialogTitle>View Restricted Client Data</DialogTitle><DialogDescription>This access is logged to Admin Activity with your ID, the record, timestamp and reason.</DialogDescription></DialogHeader>
          {!raData ? (
            <div className="space-y-3">
              <Select value={reason} onValueChange={setReason}><SelectTrigger data-testid="restricted-reason"><SelectValue placeholder="Select a reason…" /></SelectTrigger><SelectContent>{RA_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
              <Button data-testid="restricted-confirm" disabled={!reason || raBusy} onClick={doReveal} className="w-full">{raBusy ? "Accessing…" : "Confirm & View"}</Button>
            </div>
          ) : (
            <div className="space-y-1.5 text-sm" data-testid="restricted-data">
              <Row k="Client" v={raData.contact_name} />
              <Row k="Email" v={raData.contact_email} />
              <Row k="Mobile" v={raData.contact_mobile} />
              <Row k="Message" v={raData.message} />
              <Row k="Assigned Pro" v={raData.professional_name} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Fade>
  );
}

/* ------------------------------ ACTIVITY -------------------------------- */
function ActivityView({ logs }) {
  const [action, setAction] = useState("ALL");
  const actions = useMemo(() => Array.from(new Set(logs.map((l) => l.action))).sort(), [logs]);
  const filtered = action === "ALL" ? logs : logs.filter((l) => l.action === action);
  return (
    <Fade>
      <div className="flex gap-2 mb-4">
        <Select value={action} onValueChange={setAction}><SelectTrigger data-testid="filter-activity-action" className="w-[220px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ALL">All actions</SelectItem>{actions.map((a) => <SelectItem key={a} value={a}>{(a || "").replace(/_/g, " ").toLowerCase()}</SelectItem>)}</SelectContent></Select>
      </div>
      <div className="bg-card border border-border rounded-xl divide-y divide-border" data-testid="audit-log-list">
        {filtered.length === 0 && <p className="p-6 text-slate-500 text-sm">No administrator actions recorded yet.</p>}
        {filtered.map((l) => (
          <div key={l.id} className="px-5 py-3 flex items-center gap-3 text-sm">
            <Check className="h-4 w-4 text-accent shrink-0" />
            <span className="font-medium">{l.admin_name}</span>
            <span className="text-slate-500">{l.action.replace(/_/g, " ").toLowerCase()}</span>
            <span className="text-slate-400 truncate">{l.target_type} · {l.target_id}</span>
            {l.detail && <span className="text-slate-400 italic truncate">“{l.detail}”</span>}
            <span className="ml-auto text-[11px] text-slate-400 whitespace-nowrap">{timeAgo(l.created_at)}</span>
          </div>
        ))}
      </div>
    </Fade>
  );
}
